/**
 * SideNav — full-height left navigation matching the Figma desktop design.
 * Logo at top, nav items (Home active), profile + Create Post at bottom.
 * Items marked requiresAuth render greyed for logged-out visitors and trigger
 * the AuthGate prompt, on the landing page and in the auth flow alike.
 */
import {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import messageContext from '@wlb/shared/context/MessageContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { useTheme } from '@wlb/shared/theme';
import { CONVERSATIONS_PAGE_SIZE } from '@wlb/shared/constants';
import { resolveProfilePictureUrl } from '@wlb/shared/utils/imageUrl';
import {
  formatPagedUnreadBadgeCount,
  formatUnreadBadgeCount,
} from '@wlb/shared/utils/unreadMessages';
import { useAuthGate } from '../feedback/AuthGate';
import { LogoutConfirmModal } from '../../features/auth/LogoutConfirmModal';
import { useNotificationBadge } from '../../features/notifications/NotificationBadgeContext';
import { getProfileAvatarUrl, useCurrentProfile } from '../../features/profile/useCurrentProfile';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import { Badge } from '../ui/Badge';
import { Avatar } from '../ui/Avatar';
import { BrandLogo } from '../ui/BrandLogo';
import { PopoverMenu } from '../ui/PopoverMenu';
import { SecuredByAiLink } from './SecuredByAiLink';
import { SidebarRobiCard } from './SidebarRobiCard';
import {
  UserIcon,
  MoreIcon,
  LogoutIcon,
  ChevronDownIcon,
  CloseIcon,
  SunIcon,
  MoonIcon,
  MonitorIcon,
} from '../ui/icons';
import {
  MORE_ROUTES,
  MORE_SECTIONS,
  MORE_SUBMENUS,
  NAV_ITEMS,
  type MoreItem,
  type MoreSubmenu,
  type NavItem,
} from './navigation';
import './layout.css';

export function SideNav() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { preference, setPreference, isDark } = useTheme();
  const [moreOpen, setMoreOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [themeAnchor, setThemeAnchor] = useState<DOMRect | null>(null);
  const { requireAuth } = useAuthGate();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const moreToggleRef = useRef<HTMLButtonElement>(null);
  const themeToggleRef = useRef<HTMLButtonElement>(null);
  const profile = useCurrentProfile();
  const avatarUrl = getProfileAvatarUrl(profile);
  const displayName = getUsernameDisplay(profile);
  // Both read optionally: the navigation also renders for anonymous visitors,
  // who sit outside MessageProvider and have nothing to count.
  const messages = useContext(messageContext);
  const notificationBadge = useNotificationBadge();

  /** Count pill for a destination that tracks unread state, if it has any. */
  const renderBadge = (to: string) => {
    if (to === '/messages') {
      const count = messages?.unreadConversationCount ?? 0;
      const label = formatPagedUnreadBadgeCount(count, CONVERSATIONS_PAGE_SIZE);
      return label ? (
        <Badge
          label={label}
          description={t('sidebar.accessibility.unreadConversations', { amount: label })}
          className="side-nav__badge"
        />
      ) : null;
    }

    if (to === '/notifications') {
      const count = notificationBadge?.unreadCount ?? 0;
      const label = formatUnreadBadgeCount(count);
      return label ? (
        <Badge
          label={label}
          description={t('sidebar.accessibility.unreadNotifications', { amount: label })}
          className="side-nav__badge"
        />
      ) : null;
    }

    return null;
  };

  // The /robi entry stands for whichever agent the chat opens: Robi until the
  // member picks an AI Buddy, then that buddy's own name and picture.
  const aiBuddy = profile?.followedAiAgent?.agentId ? profile.followedAiAgent : null;
  const aiBuddyName = aiBuddy?.userName?.trim() ?? '';
  const aiBuddyAvatar = resolveProfilePictureUrl(aiBuddy?.profilePicture);

  // Landing on a page that lives under More keeps the section open, so the
  // sidebar always shows where the member is.
  const matchesPath = (to: string): boolean =>
    pathname === to || pathname.startsWith(`${to}/`);
  const isMoreRoute = MORE_ROUTES.some(item => matchesPath(item.to));
  const activeSubmenu =
    MORE_SUBMENUS.find(menu => menu.items.some(item => matchesPath(item.to)))?.key ?? null;

  useEffect(() => {
    if (isMoreRoute) setMoreOpen(true);
  }, [isMoreRoute]);

  useEffect(() => {
    if (activeSubmenu) setOpenSubmenu(activeSubmenu);
  }, [activeSubmenu]);

  const closeMore = (): void => {
    setMoreOpen(false);
    moreToggleRef.current?.focus();
  };

  const closeThemeMenu = (): void => {
    setThemeAnchor(null);
    themeToggleRef.current?.focus();
  };

  const themeMenuOpen = Boolean(themeAnchor);

  useEffect(() => {
    if (!themeMenuOpen) return undefined;

    let frame = 0;
    const refreshThemeAnchor = (): void => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (themeToggleRef.current) {
          setThemeAnchor(themeToggleRef.current.getBoundingClientRect());
        }
      });
    };

    // Desktop browser zoom emits a viewport resize. Remeasuring the live
    // trigger prevents the portal menu from retaining pre-zoom coordinates.
    window.addEventListener('resize', refreshThemeAnchor);
    window.visualViewport?.addEventListener('resize', refreshThemeAnchor);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', refreshThemeAnchor);
      window.visualViewport?.removeEventListener('resize', refreshThemeAnchor);
    };
  }, [themeMenuOpen]);

  const handleThemeSelect = (key: 'light' | 'dark' | 'system'): void => {
    setPreference(key);
    closeThemeMenu();
  };

  const handleLogoutRequest = (): void => {
    setMoreOpen(false);
    setLogoutConfirmOpen(true);
  };

  const handleMoreKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape' && moreOpen) {
      event.stopPropagation();
      closeMore();
    }
  };

  const renderMoreItem = (item: MoreItem, isSubItem = false) => {
    const Icon = item.icon;
    const label = t(item.labelKey);
    const gated = item.requiresAuth !== false && !isAuthenticated;
    const base = `side-nav__link side-nav__more-link${
      isSubItem ? ' side-nav__submenu-link' : ''
    }${gated ? ' side-nav__link--locked' : ''}`;

    if (gated) {
      return (
        <li key={item.labelKey}>
          <button
            className={base}
            title={t('sidebar.accessibility.signInRequired', { label })}
            onClick={() => requireAuth(() => navigate(item.to))}
          >
            <Icon size={18} />
            <span className="side-nav__label">{label}</span>
          </button>
        </li>
      );
    }

    return (
      <li key={item.labelKey}>
        <NavLink
          to={item.to}
          title={label}
          className={({ isActive }) => `${base}${isActive ? ' side-nav__link--active' : ''}`}
        >
          <Icon size={18} />
          <span className="side-nav__label">{label}</span>
        </NavLink>
      </li>
    );
  };

  const renderSubmenu = (menu: MoreSubmenu) => {
    const Icon = menu.icon;
    const label = t(menu.labelKey);
    const isOpen = openSubmenu === menu.key;
    const hasActiveChild = menu.items.some(item => matchesPath(item.to));
    // A group with nothing a visitor may open behaves like a gated row itself;
    // groups holding public pages (Support) stay expandable.
    const gated =
      !isAuthenticated && menu.items.every(item => item.requiresAuth !== false);

    return (
      <li key={menu.key}>
        <button
          className={`side-nav__link side-nav__more-link side-nav__submenu-toggle${
            isOpen ? ' side-nav__submenu-toggle--open' : ''
          }${!isOpen && hasActiveChild ? ' side-nav__link--active' : ''}${
            gated ? ' side-nav__link--locked' : ''
          }`}
          onClick={() =>
            gated
              ? requireAuth()
              : setOpenSubmenu(current => (current === menu.key ? null : menu.key))
          }
          aria-expanded={gated ? undefined : isOpen}
          aria-controls={gated ? undefined : `sidebar-more-${menu.key}`}
          title={gated ? t('sidebar.accessibility.signInRequired', { label }) : label}
        >
          <Icon size={18} />
          <span className="side-nav__label">{label}</span>
          <ChevronDownIcon size={16} className="side-nav__more-chevron" aria-hidden />
        </button>
        {isOpen && (
          <ul id={`sidebar-more-${menu.key}`} className="side-nav__submenu-list">
            {menu.items.map(item => renderMoreItem(item, true))}
          </ul>
        )}
      </li>
    );
  };

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const isAiBuddyItem = item.to === '/robi';
    const label = isAiBuddyItem && aiBuddyName ? aiBuddyName : t(item.labelKey);
    const gated = item.requiresAuth && !isAuthenticated;
    const base = `side-nav__link${item.primaryAction ? ' side-nav__link--primary-action' : ''}${
      gated ? ' side-nav__link--locked' : ''
    }`;
    const icon =
      isAiBuddyItem && aiBuddyAvatar ? (
        <img className="side-nav__ai-avatar" src={aiBuddyAvatar} alt="" aria-hidden />
      ) : item.primaryAction ? (
        <span className="side-nav__create-icon" aria-hidden>
          <Icon size={16} />
        </span>
      ) : (
        <Icon size={20} />
      );

    if (gated) {
      return (
        <button
          key={item.to}
          className={base}
          title={t('sidebar.accessibility.signInRequired', { label })}
          onClick={() => requireAuth(() => navigate(item.to))}
        >
          {icon}
          <span className="side-nav__label">{label}</span>
        </button>
      );
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        className={({ isActive }) => `${base}${isActive ? ' side-nav__link--active' : ''}`}
      >
        {icon}
        <span className="side-nav__label">{label}</span>
        {renderBadge(item.to)}
      </NavLink>
    );
  };

  return (
    <nav className="side-nav" aria-label={t('drawer.home')}>
      <div className="side-nav__top">
        <Link to="/" className="side-nav__logo" aria-label={t('home.accessibility.homeLink')}>
          <BrandLogo />
        </Link>
        <button
          ref={themeToggleRef}
          type="button"
          className="side-nav__theme-toggle"
          onClick={event => setThemeAnchor(event.currentTarget.getBoundingClientRect())}
          aria-label={t('common.theme.toggle')}
          aria-haspopup="menu"
          aria-expanded={themeMenuOpen}
          title={t('common.theme.menuLabel')}
        >
          {isDark ? <MoonIcon size={20} /> : <SunIcon size={20} />}
        </button>
      </div>

      {/* Escape the sticky sidebar's stacking context so feed headers and post
          controls cannot paint over this viewport-level menu. */}
      {createPortal(
        <PopoverMenu
          anchorRect={themeAnchor}
          density="compact"
          label={t('common.theme.menuLabel')}
          items={[
            { key: 'light', label: t('common.theme.light'), icon: SunIcon, selected: preference === 'light' },
            { key: 'dark', label: t('common.theme.dark'), icon: MoonIcon, selected: preference === 'dark' },
            { key: 'system', label: t('common.theme.system'), icon: MonitorIcon, selected: preference === 'system' },
          ]}
          onClose={closeThemeMenu}
          onSelect={handleThemeSelect}
        />,
        document.body,
      )}

      <ul className="side-nav__list">
        {NAV_ITEMS.map(item => (
          <li
            key={item.to}
            className={item.hideOnMobileTabBar ? 'side-nav__item--menu-only' : undefined}
            // Only read inside the bottom-tab-bar breakpoint; the sidebar
            // keeps the declaration order.
            style={{ '--mobile-tab-order': item.mobileTabOrder } as CSSProperties}
          >
            {renderItem(item)}
          </li>
        ))}
      </ul>

      <div className="side-nav__spacer" />

      {!moreOpen && <SidebarRobiCard />}

      <div className="side-nav__more" onKeyDown={handleMoreKeyDown}>
        <button
          ref={moreToggleRef}
          className={`side-nav__link side-nav__more-toggle${
            moreOpen ? ' side-nav__more-toggle--open' : ''
          }${!moreOpen && isMoreRoute ? ' side-nav__link--active' : ''}`}
          onClick={() => setMoreOpen(open => !open)}
          aria-expanded={moreOpen}
          aria-controls="sidebar-more-menu"
          title={t('home.rightBanner.more')}
        >
          <MoreIcon size={20} />
          <span className="side-nav__label">{t('home.rightBanner.more')}</span>
          {/* The row itself toggles; the X marks the open state as closable. */}
          {moreOpen && <CloseIcon size={16} className="side-nav__more-close" aria-hidden />}
        </button>

        {moreOpen && (
          <ul id="sidebar-more-menu" className="side-nav__more-list">
            {MORE_SECTIONS.map((section, index) => (
              <Fragment key={section[0].labelKey}>
                {index > 0 && <li className="side-nav__menu-divider" role="separator" />}
                {section.map(item => renderMoreItem(item))}
              </Fragment>
            ))}
            <li className="side-nav__menu-divider" role="separator" />
            {MORE_SUBMENUS.map(renderSubmenu)}
          </ul>
        )}
      </div>

      {isAuthenticated ? (
        <div className="side-nav__profile-row">
          <button
            className="side-nav__profile"
            onClick={() => navigate('/profile')}
          >
            <Avatar
              src={avatarUrl}
              name={displayName}
              username={profile?.userName}
              seed={profile?.userGuid ?? profile?.userId}
              size="md"
              className="side-nav__profile-avatar"
            />
            <div className="side-nav__profile-info">
              <span className="side-nav__profile-name">{displayName || t('home.rightBanner.myProfile')}</span>
            </div>
          </button>
          <button
            className="side-nav__logout"
            onClick={handleLogoutRequest}
            title={t('sidebar.logout')}
            aria-label={t('sidebar.logout')}
          >
            <LogoutIcon size={20} />
          </button>
        </div>
      ) : (
        <div className="side-nav__guest">
          <span className="side-nav__profile-avatar" aria-hidden>
            <UserIcon size={20} />
          </span>
          <div className="side-nav__guest-info">
            <span className="side-nav__profile-name">{t('home.rightBanner.guestName')}</span>
            <button
              className="side-nav__guest-signup"
              onClick={() => navigate('/signup')}
            >
              {t('home.rightBanner.signUp')}
            </button>
          </div>
        </div>
      )}

      <SecuredByAiLink variant="rail" />

      {/* Portalled for the same reason as the theme menu: the rail becomes a
          stacking context on small screens and would trap the dialog. */}
      {logoutConfirmOpen &&
        createPortal(
          <LogoutConfirmModal onClose={() => setLogoutConfirmOpen(false)} />,
          document.body,
        )}
    </nav>
  );
}
